import type { RecordStatusTone } from '@traceio-org/ui';
import {
  KEY_PROJECTS,
  keyProjectName,
  type AdminKey,
  type KeyCounts,
  type KeyKind,
  type KeyProject,
  type KeyStatus,
} from '@traceio-org/platform-contracts';
import { formatCount, formatDate } from './format';
import { PROVIDER_LOGOS, providerLabel } from './keyProviders';

/** The Keys page's words and filters, pure (test/keys-rules.test.ts). */

export const KIND_LABELS: Readonly<Record<KeyKind, string>> = {
  provider_token: 'Provider token',
  signing_secret: 'Signing secret',
  encryption_key: 'Encryption key',
};

export const STATUS_LABELS: Readonly<Record<KeyStatus, { label: string; tone: RecordStatusTone }>> = {
  expired: { label: 'Expired', tone: 'danger' },
  due: { label: 'Due', tone: 'warning' },
  not_set: { label: 'Not set', tone: 'neutral' },
  ok: { label: 'OK', tone: 'positive' },
  no_expiry: { label: 'No expiry', tone: 'neutral' },
};

export const KEY_TABS = [
  { id: 'attention', label: 'Needs attention' },
  { id: 'all', label: 'All' },
  { id: 'provider_token', label: 'Provider tokens' },
  { id: 'signing_secret', label: 'Signing secrets' },
  { id: 'encryption_key', label: 'Encryption keys' },
  { id: 'hidden', label: 'Hidden' },
] as const;
export type KeyTab = (typeof KEY_TABS)[number]['id'];

/** The tab of the address (?tab=); Needs attention unless it names another. */
export function tabParam(value: string | null): KeyTab {
  return KEY_TABS.find((tab) => tab.id === value)?.id ?? 'attention';
}

/** Needs attention: expired, due, or a provider token whose expiry nobody recorded. */
const ATTENTION: ReadonlySet<KeyStatus> = new Set(['expired', 'due', 'not_set']);

/**
 * Every tab but Hidden shows tracked entries only (secrets by the name's rules, or tracked by staff); Hidden shows the
 * rest (configuration by the rules, or Not a secret).
 */
export function inTab(key: Pick<AdminKey, 'status' | 'kind' | 'tracked'>, tab: KeyTab): boolean {
  if (tab === 'hidden') return !key.tracked;
  if (!key.tracked) return false;
  if (tab === 'all') return true;
  if (tab === 'attention') return ATTENTION.has(key.status);
  return key.kind === tab;
}

export function tabCounts(
  keys: readonly Pick<AdminKey, 'status' | 'kind' | 'tracked'>[],
): Record<KeyTab, number> {
  return Object.fromEntries(
    KEY_TABS.map((tab) => [tab.id, keys.filter((key) => inTab(key, tab.id)).length]),
  ) as Record<KeyTab, number>;
}

export function providerName(key: Pick<AdminKey, 'provider' | 'providerName'>): string {
  return providerLabel(key.provider, key.providerName);
}

/**
 * True when Keys reads more than one Infisical project. With one (today: trace only, Trace Enterprise is deprecated) the
 * page shows no project filter and no project name on rows, since both would say the same thing on every row.
 */
export const MULTIPLE_PROJECTS: boolean = KEY_PROJECTS.length > 1;

/** The project filter: every project, or one of them (shown only with MULTIPLE_PROJECTS). */
export const PROJECT_FILTERS = [
  { value: 'all', label: 'All projects' },
  ...KEY_PROJECTS.map((project) => ({ value: project.id, label: project.name })),
] as const;
export type ProjectFilter = 'all' | KeyProject;

/** The project of the address (?project=); every project unless it names one of them. */
export function projectParam(value: string | null): ProjectFilter {
  return KEY_PROJECTS.find((project) => project.id === value)?.id ?? 'all';
}

export function inProject(key: Pick<AdminKey, 'project'>, filter: ProjectFilter): boolean {
  return filter === 'all' || key.project === filter;
}

/** Where a key lives, in words: its folder ("/trace-platform"), after its project when there are several. */
export function placeOf(key: Pick<AdminKey, 'project' | 'folder'>): string {
  return MULTIPLE_PROJECTS ? `${keyProjectName(key.project)} · ${key.folder}` : key.folder;
}

/**
 * True when the provider itself ends its keys (DigitalOcean's tokens, an Infisical client secret's time to live): the
 * dialog says Set expiry. Everywhere else the day is a rotation date staff choose (Set rotation date), same control.
 */
export function hasRealExpiry(key: Pick<AdminKey, 'name' | 'provider'>): boolean {
  if (/^INFISICAL_/.test(key.name)) return true;
  return key.provider !== null && PROVIDER_LOGOS[key.provider].expires;
}

/** Search: by any word of the name, folder, what uses it, the provider or the kind, in any case. */
export function matchesSearch(key: AdminKey, text: string): boolean {
  const query = text.trim().toLowerCase();
  if (!query) return true;
  const haystack = [
    key.name,
    key.pairedName ?? '',
    key.folder,
    keyProjectName(key.project),
    key.usedBy,
    providerName(key),
    KIND_LABELS[key.kind],
  ]
    .join(' ')
    .toLowerCase();
  return query.split(/\s+/).every((word) => haystack.includes(word));
}

/** "today", "tomorrow", "in 17 days", "yesterday", "4 days ago". */
export function relativeDays(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${formatCount(days)} days` : `${formatCount(-days)} days ago`;
}

/** A UTC day (YYYY-MM-DD) in the viewer's locale. */
export function formatDay(day: string): string {
  return formatDate(`${day}T00:00:00.000Z`);
}

/** The Expires cell: the day (or what stands for it) and, under it, where it comes from and how far it is. */
export function expiresCell(key: AdminKey): { primary: string; secondary: string | null } {
  if (key.expirySource === 'set_by_you' && key.expiresOn) {
    const source = hasRealExpiry(key) ? 'Set by you' : 'Rotation date';
    return {
      primary: formatDay(key.expiresOn),
      secondary: key.daysLeft === null ? source : `${source} · ${relativeDays(key.daysLeft)}`,
    };
  }
  if (key.expirySource === 'does_not_expire') return { primary: 'Never', secondary: "Doesn't expire" };
  if (key.status === 'not_set') return { primary: 'Unknown', secondary: 'Not recorded yet' };
  return { primary: 'Never', secondary: "Trace's own, doesn't expire" };
}

/**
 * The actions of a row. Rotating is offered for a key past or near its expiry, never for an encryption key (rotating one
 * means re-encrypting data, so the page never nudges it). Recording is for platform admins only: Set expiry where
 * nothing is recorded yet, Edit otherwise, Choose provider where no rule matched the name, Change provider where a
 * choice is saved, and Not a secret (tracked rows) or Track this (hidden rows). A hidden row offers Track this only.
 */
export function rowActions(
  key: Pick<AdminKey, 'status' | 'kind' | 'provider' | 'providerChosen' | 'expirySource' | 'tracked'>,
  canManage: boolean,
): {
  rotate: boolean;
  expiry: 'Set expiry' | 'Edit' | null;
  provider: 'Choose provider' | 'Change provider' | null;
  tracking: 'Not a secret' | 'Track this' | null;
} {
  if (!key.tracked) {
    return { rotate: false, expiry: null, provider: null, tracking: canManage ? 'Track this' : null };
  }
  return {
    tracking: canManage ? 'Not a secret' : null,
    rotate: (key.status === 'expired' || key.status === 'due') && key.kind !== 'encryption_key',
    expiry: canManage ? (key.expirySource === null ? 'Set expiry' : 'Edit') : null,
    // Unmatched names get Choose provider; a choice already saved can be changed or undone.
    provider: !canManage
      ? null
      : key.provider === null
        ? 'Choose provider'
        : key.providerChosen
          ? 'Change provider'
          : null,
  };
}

/** The summary strip: expired, due, expiry not set, and every key tracked. */
export function summaryMetrics(
  counts: KeyCounts,
): { label: string; value: number; tone: 'danger' | 'warning' | null }[] {
  return [
    { label: 'Expired', value: counts.expired, tone: counts.expired > 0 ? 'danger' : null },
    { label: 'Due soon', value: counts.due, tone: counts.due > 0 ? 'warning' : null },
    { label: 'Expiry not set', value: counts.notSet, tone: null },
    { label: 'Tracked', value: counts.tracked, tone: null },
  ];
}

/**
 * Who makes the new value: an Infisical machine identity (INFISICAL_* names: a new client secret on the identity, never
 * a random value, which would lock the identity out), Trace itself (a random secret), a known provider (its page), or
 * a provider nobody named yet (the provider that issued it, in general words).
 */
export type RotationSource = 'infisical' | 'trace' | 'provider' | 'unknown';

export function rotationSourceOf(key: Pick<AdminKey, 'name' | 'provider'>): RotationSource {
  if (/^INFISICAL_/.test(key.name)) return 'infisical';
  if (key.provider === 'trace') return 'trace';
  return key.provider === null || key.provider === 'other' ? 'unknown' : 'provider';
}

const DAY_MS = 86_400_000;

/** A UTC day plus days. */
export function addDays(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** A UTC day plus calendar months, the day of the month kept or, past the month's end, its last day (Aug 31 + 6 = Feb 28). */
export function addMonths(day: string, months: number): string {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  const first = new Date(Date.UTC(year, month - 1 + months, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), Math.min(date, last)))
    .toISOString()
    .slice(0, 10);
}

/** The quick choices of the expiry dialog, from today (UTC). */
export const QUICK_EXPIRIES = [
  { label: '30 days', of: (today: string) => addDays(today, 30) },
  { label: '60 days', of: (today: string) => addDays(today, 60) },
  { label: '90 days', of: (today: string) => addDays(today, 90) },
  { label: '6 months', of: (today: string) => addMonths(today, 6) },
  { label: '1 year', of: (today: string) => addMonths(today, 12) },
] as const;

/** True when a failure of the last sync covers the key: its project (folder /), or its folder and those below it. */
export function failureCovers(
  failure: { project: string; folder: string },
  key: { project: string; folder: string },
): boolean {
  if (failure.project !== key.project) return false;
  return (
    failure.folder === '/' || key.folder === failure.folder || key.folder.startsWith(`${failure.folder}/`)
  );
}
